import { Rect, Textbox, Circle, Triangle, FabricImage } from 'fabric';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const applyCommonProps = (object, layer = {}) => {
  if (!object || !layer || typeof layer !== 'object') return object;

  object.set({
    left: toNumber(layer.left, 0),
    top: toNumber(layer.top, 0),
    angle: toNumber(layer.angle, 0),
    opacity: layer.opacity == null ? 1 : Math.max(0, Math.min(1, Number(layer.opacity))),
    selectable: Boolean(layer.selectable),
    evented: Boolean(layer.evented),
  });

  if (layer.id) object.set('mpObjectId', String(layer.id));
  if (layer.role) object.set('mpRole', String(layer.role));

  return object;
};

const buildRect = (layer) => new Rect({
  width: toNumber(layer.width, 120),
  height: toNumber(layer.height, 120),
  rx: toNumber(layer.rx, 0),
  ry: toNumber(layer.ry, 0),
  fill: layer.fill || '#111827',
  stroke: layer.stroke || undefined,
  strokeWidth: toNumber(layer.strokeWidth, 0),
});

const buildCircle = (layer) => new Circle({
  radius: toNumber(layer.radius, 60),
  fill: layer.fill || '#111827',
  stroke: layer.stroke || undefined,
  strokeWidth: toNumber(layer.strokeWidth, 0),
});

const buildTriangle = (layer) => new Triangle({
  width: toNumber(layer.width, 120),
  height: toNumber(layer.height, 120),
  fill: layer.fill || '#111827',
  stroke: layer.stroke || undefined,
  strokeWidth: toNumber(layer.strokeWidth, 0),
});

const buildTextbox = (layer) => new Textbox(String(layer.text || ''), {
  width: toNumber(layer.width, 400),
  fontSize: toNumber(layer.fontSize, 34),
  fontFamily: String(layer.fontFamily || 'Inter'),
  fontWeight: String(layer.fontWeight || '400'),
  fill: String(layer.fill || '#ffffff'),
  lineHeight: toNumber(layer.lineHeight, 1.2),
  charSpacing: toNumber(layer.charSpacing, 0),
  textAlign: String(layer.textAlign || 'left'),
});

const applyImageSize = (image, layer = {}) => {
  const targetWidth = toNumber(layer.width, 0);
  const targetHeight = toNumber(layer.height, 0);
  const fit = String(layer.fit || '').toLowerCase();

  if (!targetWidth && !targetHeight) return;

  if (targetWidth && targetHeight && fit === 'cover') {
    const imageWidth = image.width || targetWidth;
    const imageHeight = image.height || targetHeight;
    const scale = Math.max(targetWidth / imageWidth, targetHeight / imageHeight);

    image.set({
      scaleX: scale,
      scaleY: scale,
      cropX: Math.max(0, ((imageWidth * scale) - targetWidth) / (2 * scale)),
      cropY: Math.max(0, ((imageHeight * scale) - targetHeight) / (2 * scale)),
      width: targetWidth / scale,
      height: targetHeight / scale,
    });

    return;
  }

  if (targetWidth && targetHeight) {
    image.scaleToWidth(targetWidth);
    if ((image.getScaledHeight?.() || 0) > targetHeight) {
      image.scaleToHeight(targetHeight);
    }
    return;
  }

  if (targetWidth) image.scaleToWidth(targetWidth);
  if (targetHeight) image.scaleToHeight(targetHeight);
};

export async function buildFabricObjectFromLayer(layer = {}) {
  if (!layer || typeof layer !== 'object') return null;

  const type = String(layer.type || '').toLowerCase();

  if (type === 'rect') return applyCommonProps(buildRect(layer), layer);
  if (type === 'circle') return applyCommonProps(buildCircle(layer), layer);
  if (type === 'triangle') return applyCommonProps(buildTriangle(layer), layer);
  if (type === 'text' || type === 'textbox') return applyCommonProps(buildTextbox(layer), layer);

  if (type === 'image') {
    const sourceUrl = String(layer.src || layer.url || '');
    if (!sourceUrl) return null;
    const image = await FabricImage.fromURL(sourceUrl, { crossOrigin: 'anonymous' });
    applyImageSize(image, layer);
    return applyCommonProps(image, layer);
  }

  return null;
}
